// Seasonal effects and holiday themes around the course. The season brings falling snow and
// birch leaves; each holiday theme (calendar.ts) dresses the stadium: pennant and festoon colours,
// and its own pieces — a Christmas tree, jack-o'-lanterns, balloons, Easter eggs, the Midsummer
// bonfire, New Year's fireworks — plus confetti, hearts or bats. Pieces are built only while their
// theme is on and are disposed when it ends.
import {
  AdditiveBlending, Color, ConeGeometry, CylinderGeometry, DoubleSide, ExtrudeGeometry, Group, InstancedMesh,
  Matrix4, Mesh, MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, Quaternion, SRGBColorSpace, Shape, SphereGeometry,
  Uniform, Vector3, type BufferGeometry, type Camera, type Material, type Object3D,
} from 'three';
import { CATS } from '../../data/cats';
import { TRACK_LENGTH } from '../../data/track';
import type { SeasonState, ThemeId } from './calendar';
import type { Rgb } from './daylight';
import { Fireworks } from './fireworks';
import { COURSE, HOARDING, LAKE, PODIUM, STAND, rng, standModulesX } from './layout';
import { Particles, type ParticleKind } from './particles';
import { GLSL_NOISE, merge, place } from './util';

type Piece = 'tree' | 'pumpkins' | 'balloons' | 'eggs' | 'bonfire' | 'fireworks';

interface ThemeLook {
  flags?: string[];
  bulbs?: string[];
  particles?: { kind: ParticleKind; palette?: Rgb[] }[];
  pieces: Piece[];
  balloons?: string[];
}

const hexRgb = (h: string): Rgb => { const c = new Color(h); return [c.r, c.g, c.b]; };
/** The six cats' accent colours (the site's hsl(h 90% 70%)), as hex. */
const CAT_COLOURS = CATS.map((c) => `#${new Color().setHSL(c.accentHue / 360, 0.9, 0.7, SRGBColorSpace).getHexString()}`);

/** How each theme dresses the stadium. Earlier themes in this list win the pennant and bulb colours. */
export const THEME_LOOKS: Record<ThemeId, ThemeLook> = {
  independence: { flags: ['#ffffff', '#002f6c'], bulbs: ['#ffffff', '#3a6fff'], pieces: [] },
  newYear: { flags: ['#f2c35b', '#f6efe2', '#c0c4cc'], bulbs: ['#ffe6a0', '#ffffff', '#ffd23f'], pieces: ['fireworks'] },
  valentine: {
    flags: ['#ff3f6e', '#ff9fbf', '#ffffff'], bulbs: ['#ff4f7a', '#ffb0c8'], pieces: [],
    particles: [{ kind: 'hearts' }],
  },
  easter: {
    flags: ['#ffe066', '#c9a0ff', '#9be27a', '#ffffff'], bulbs: ['#fff0a0', '#e0c8ff', '#c8ffb0'], pieces: ['eggs'],
    particles: [{ kind: 'confetti', palette: ['#ffe066', '#c9a0ff', '#9be27a', '#ffb3d1'].map(hexRgb) }],
  },
  vappu: {
    flags: ['#ff3fa4', '#ffd23f', '#3fa9ff', '#5fe07a', '#ffffff'], bulbs: ['#ff6fb0', '#ffe066', '#7fc6ff', '#8af59a'],
    pieces: ['balloons'], balloons: ['#ff3f6e', '#ffd23f', '#3fa9ff', '#5fe07a', '#b46cff', '#ffffff'],
    particles: [{ kind: 'confetti' }],
  },
  midsummer: { flags: ['#ffffff', '#002f6c'], pieces: ['bonfire'] },
  catDay: {
    flags: CAT_COLOURS, bulbs: CAT_COLOURS, pieces: ['balloons'], balloons: CAT_COLOURS,
    particles: [{ kind: 'confetti', palette: CAT_COLOURS.slice(0, 4).map(hexRgb) }],
  },
  halloween: {
    flags: ['#ff7a1a', '#1a1220', '#8a4dff'], bulbs: ['#ff8a2a', '#b46cff', '#9cff5a'], pieces: ['pumpkins'],
    particles: [{ kind: 'bats' }],
  },
  christmas: {
    flags: ['#c8102e', '#1f7a3a', '#ffffff', '#f2c35b'], bulbs: ['#ff3030', '#ffd23f', '#3fd06a', '#ffffff', '#4f8aff'],
    pieces: ['tree'],
  },
};

/** The order theme colours are chosen in (the first active theme wins). */
const PRIORITY: ThemeId[] = ['independence', 'newYear', 'valentine', 'easter', 'vappu', 'midsummer', 'catDay', 'halloween', 'christmas'];

/** Pennant and festoon colours for a set of themes: the most specific theme wins; null keeps the stadium's own. */
export function themeColours(themes: ThemeId[]): { flags: string[] | null; bulbs: string[] | null } {
  const ordered = PRIORITY.filter((t) => themes.includes(t));
  return {
    flags: ordered.map((t) => THEME_LOOKS[t].flags).find((f) => f) ?? null,
    bulbs: ordered.map((t) => THEME_LOOKS[t].bulbs).find((b) => b) ?? null,
  };
}

/** What the stadium needs from the season and the light each frame. */
export interface SeasonalFrame {
  timeS: number;
  camera: Camera & { fov?: number };
  viewportHeight: number;
  /** Darkness: 0 by day, 1 at night (the floodlights' level). */
  night: number;
  /** Light on lit particles. */
  light: Rgb;
  /** Joutseno local hour, for the bonfire. */
  hour: number;
}

export class Seasonal {
  readonly group = new Group();
  private snow = new Particles('snow');
  private leaves = new Particles('leaves');
  private themes: ThemeId[] = [];
  private built: { piece: Piece | ParticleKind; objects: Object3D[]; update?: (f: SeasonalFrame) => void; dispose: () => void }[] = [];
  private particles: Particles[] = [];

  constructor() {
    this.group.name = 'seasonal';
    this.snow.amount = 0; this.leaves.amount = 0;
    this.group.add(this.snow.points, this.leaves.points);
  }

  /** Falling snow and leaves for the season. */
  setSeason(s: SeasonState): void {
    this.snow.amount = s.snowfall;
    this.leaves.amount = s.leafFall;
  }

  /** Switches the holiday pieces and particles to a new set of themes. */
  setThemes(themes: ThemeId[]): void {
    if (themes.join() === this.themes.join()) return;
    this.themes = [...themes];
    for (const b of this.built) { for (const o of b.objects) this.group.remove(o); b.dispose(); }
    this.built = []; this.particles = [];
    const pieces = new Set<Piece>();
    for (const t of themes) for (const p of THEME_LOOKS[t].pieces) pieces.add(p);
    const balloonColours = themes.map((t) => THEME_LOOKS[t].balloons).find((b) => b) ?? [];
    for (const p of pieces) {
      const b = p === 'tree' ? christmasTree() : p === 'pumpkins' ? pumpkins() : p === 'balloons' ? balloons(balloonColours)
        : p === 'eggs' ? eggs() : p === 'bonfire' ? bonfire() : fireworks();
      this.built.push({ piece: p, ...b });
      this.group.add(...b.objects);
    }
    for (const t of themes) for (const spec of THEME_LOOKS[t].particles ?? []) {
      const layer = new Particles(spec.kind, spec.palette);
      this.particles.push(layer);
      this.built.push({ piece: spec.kind, objects: [layer.points], dispose: () => layer.dispose() });
      this.group.add(layer.points);
    }
  }

  /** Names of the pieces and particle layers on show, for tests. */
  get pieces(): string[] { return this.built.map((b) => b.piece); }

  update(f: SeasonalFrame): void {
    for (const p of [this.snow, this.leaves, ...this.particles]) {
      if (!p.points.visible) continue;
      p.setLight(f.light);
      p.update(f.timeS, f.camera, f.viewportHeight);
    }
    for (const b of this.built) b.update?.(f);
  }
}

type Built = { objects: Object3D[]; update?: (f: SeasonalFrame) => void; dispose: () => void };

function disposer(objects: Object3D[]): () => void {
  return () => {
    for (const root of objects) root.traverse((o) => {
      const m = o as Mesh;
      if (m.geometry) m.geometry.dispose();
      const mat = m.material as Material | Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose()); else mat?.dispose();
    });
  };
}

/** A decorated Christmas spruce beside the podium, with blinking coloured lights and a gold star. */
function christmasTree(): Built {
  const at = new Vector3(PODIUM.x + 8, 0, PODIUM.z + 2.5);
  const group = new Group();
  group.name = 'christmas-tree';
  const r = rng(1224);
  const H = 9, tiers = 7;
  const parts: BufferGeometry[] = [place(new CylinderGeometry(0.16, 0.24, 1.6, 8), [0, 0.8, 0])];
  for (let i = 0; i < tiers; i++) {
    const t = i / tiers;
    const radius = 2.8 * (1 - t) + 0.4;
    const y = 1.0 + t * (H - 2.2);
    parts.push(place(new ConeGeometry(radius, 2.4, 18, 2, true), [0, y + 1.2, 0], [0, r() * 3, 0]));
  }
  const needles = new Mesh(merge(parts), new MeshStandardMaterial({ color: '#163d22', roughness: 0.85, side: DoubleSide }));
  needles.castShadow = true; needles.receiveShadow = true;
  group.add(needles);
  // Lights in a spiral round the tree.
  const n = 140;
  const bulbMat = new MeshBasicMaterial({ color: new Color(4, 4, 4) });
  const bulbs = new InstancedMesh(new SphereGeometry(0.08, 8, 6), bulbMat, n);
  const colours = ['#ff3030', '#ffd23f', '#3fd06a', '#4f8aff', '#ffffff'].map((c) => new Color(c));
  const m = new Matrix4(), q = new Quaternion(), one = new Vector3(1, 1, 1), p = new Vector3();
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const y = 1.3 + t * (H - 2.0);
    const radius = (2.8 * (1 - (y - 1.0) / (H - 2.2)) + 0.4) * 0.9 + 0.05;
    const a = t * Math.PI * 2 * 7;
    bulbs.setMatrixAt(i, m.compose(p.set(Math.cos(a) * radius, y + 0.2, Math.sin(a) * radius), q, one));
    bulbs.setColorAt(i, colours[i % colours.length]!);
  }
  group.add(bulbs);
  // A five-point gold star on top.
  const star = new Shape();
  for (let k = 0; k < 10; k++) {
    const rad = k % 2 ? 0.26 : 0.62, a = Math.PI / 2 + (k * Math.PI) / 5;
    if (k === 0) star.moveTo(Math.cos(a) * rad, Math.sin(a) * rad); else star.lineTo(Math.cos(a) * rad, Math.sin(a) * rad);
  }
  const starMesh = new Mesh(new ExtrudeGeometry(star, { depth: 0.12, bevelEnabled: false }).center(), new MeshBasicMaterial({ color: new Color(3.2, 2.4, 0.8) }));
  starMesh.position.set(0, H + 0.6, 0);
  group.add(starMesh);
  group.position.copy(at);
  const base = colours.map((c) => c.clone());
  let lastStep = -1;
  return {
    objects: [group],
    update: (f) => {
      // Blink in slow steps; brighter at night.
      const step = Math.floor(f.timeS * 1.5);
      bulbMat.color.setScalar(1.5 + 3 * f.night);
      starMesh.rotation.y = f.timeS * 0.4;
      if (step === lastStep) return;
      lastStep = step;
      const c = new Color();
      for (let i = 0; i < n; i++) {
        const on = (i + step) % 3 !== 0;
        bulbs.setColorAt(i, c.copy(base[i % base.length]!).multiplyScalar(on ? 1 : 0.15));
      }
      bulbs.instanceColor!.needsUpdate = true;
    },
    dispose: disposer([group]),
  };
}

/** Jack-o'-lanterns along both sides of the course, carved faces glowing towards the track. */
function pumpkins(): Built {
  const spots: { x: number; z: number; face: number; s: number }[] = [];
  const r = rng(1031);
  for (let x = COURSE.from + 6; x < COURSE.to - 4; x += 13) {
    // Lake side: on the apron inside the hoarding, so the track cameras see them; grandstand side: on the walkway.
    spots.push({ x: x + r() * 3, z: HOARDING.z - 0.9, face: Math.PI, s: 0.8 + r() * 0.5 });
    spots.push({ x: x + 6 + r() * 3, z: -(HOARDING.z + 0.75), face: 0, s: 0.8 + r() * 0.5 });
  }
  // Ribbed pumpkin: a squashed sphere with eight lobes.
  const body = new SphereGeometry(1, 24, 16);
  const pos = body.getAttribute('position');
  const v = new Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const lobe = 1 + 0.07 * Math.cos(Math.atan2(v.z, v.x) * 8);
    pos.setXYZ(i, v.x * lobe * 0.36, v.y * 0.27 + 0.26, v.z * lobe * 0.36);
  }
  body.computeVertexNormals();
  const glow = new Uniform(1);
  const time = new Uniform(0);
  const mat = new MeshStandardMaterial({ color: '#e0661a', roughness: 0.55 });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uGlow = glow; shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vObj;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvObj = vec3(position.x / 0.36, (position.y - 0.26) / 0.27, position.z / 0.36);');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vObj;\nuniform float uGlow, uTime;\n${GLSL_NOISE}`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        // The carved face on the +z side: two triangle eyes, a nose and a toothy grin.
        float u = vObj.x, v = vObj.y;
        float front = step(0.25, vObj.z);
        float eyeV = clamp((0.42 - v) / 0.28, 0.0, 1.0);
        float eyes = step(0.14, v) * step(v, 0.42) * step(abs(abs(u) - 0.36), 0.16 * eyeV);
        float nose = step(-0.06, v) * step(v, 0.1) * step(abs(u), 0.08 * (0.1 - v) / 0.16);
        float top = -0.12 - 0.3 * u * u + 0.07 * step(0.5, fract(u * 4.0 + 0.25)) * step(abs(u), 0.42);
        float bottom = -0.46 + 0.4 * u * u;
        float mouth = step(abs(u), 0.58) * step(bottom, v) * step(v, top);
        float carved = front * max(max(eyes, nose), mouth);
        float flicker = 0.8 + 0.2 * envNoise(vec2(uTime * 6.0, vObj.x * 2.0 + 7.0));
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.08, 0.03, 0.0), carved);
        totalEmissiveRadiance += carved * vec3(1.0, 0.5, 0.1) * uGlow * flicker;`);
  };
  mat.customProgramCacheKey = () => 'jack-o-lantern';
  const lanterns = new InstancedMesh(body, mat, spots.length);
  const stems = new InstancedMesh(place(new CylinderGeometry(0.03, 0.05, 0.14, 6), [0, 0.58, 0], [0.2, 0, 0.1]),
    new MeshStandardMaterial({ color: '#4a3a1a', roughness: 0.9 }), spots.length);
  const m = new Matrix4(), q = new Quaternion(), up = new Vector3(0, 1, 0), p = new Vector3(), s = new Vector3();
  spots.forEach((sp, i) => {
    m.compose(p.set(sp.x, 0, sp.z), q.setFromAxisAngle(up, sp.face + (r() - 0.5) * 0.5), s.setScalar(sp.s));
    lanterns.setMatrixAt(i, m); stems.setMatrixAt(i, m);
  });
  for (const im of [lanterns, stems]) { im.castShadow = true; im.receiveShadow = true; im.frustumCulled = false; im.name = 'jack-o-lanterns'; }
  return {
    objects: [lanterns, stems],
    update: (f) => { time.value = f.timeS; glow.value = 0.6 + 4.4 * f.night; },
    dispose: disposer([lanterns, stems]),
  };
}

/** Bunches of balloons tied to the hoardings on both sides, bobbing in the breeze. */
function balloons(colours: string[]): Built {
  const parts = [
    place(new SphereGeometry(0.3, 14, 10), [0, 1.95, 0], [0, 0, 0], [1, 1.18, 1]),
    place(new ConeGeometry(0.05, 0.08, 6), [0, 1.57, 0], [Math.PI, 0, 0]),
    place(new CylinderGeometry(0.006, 0.006, 1.55, 3), [0, 0.775, 0]),
  ];
  const geo = merge(parts);
  const time = new Uniform(0);
  const mat = new MeshStandardMaterial({ roughness: 0.3, metalness: 0.05 });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uTime;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vec4 bb = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        float k = position.y / 1.95;
        transformed.x += sin(uTime * 1.1 + bb.x * 0.7) * 0.22 * k * k;
        transformed.z += cos(uTime * 0.9 + bb.x * 0.5) * 0.14 * k * k;`);
  };
  mat.customProgramCacheKey = () => 'balloons';
  const spots: Vector3[] = [];
  const r = rng(501);
  for (let x = COURSE.from + 4; x < COURSE.to - 4; x += 9) {
    for (const side of [1, -1]) for (let k = 0; k < 3; k++) {
      spots.push(new Vector3(x + side * 2 + (r() - 0.5) * 0.6, HOARDING.height - 0.1, side * HOARDING.z + (r() - 0.5) * 0.3));
    }
  }
  const im = new InstancedMesh(geo, mat, spots.length);
  const list = (colours.length ? colours : ['#ff3f6e']).map((c) => new Color(c));
  const m = new Matrix4(), q = new Quaternion(), s = new Vector3();
  spots.forEach((p, i) => {
    const tilt = new Quaternion().setFromAxisAngle(new Vector3((r() - 0.5), 0, (r() - 0.5)).normalize(), 0.15 + r() * 0.15);
    im.setMatrixAt(i, m.compose(p, q.copy(tilt), s.setScalar(0.85 + r() * 0.3)));
    im.setColorAt(i, list[Math.floor(r() * list.length)]!);
  });
  im.castShadow = true; im.frustumCulled = false; im.name = 'balloons';
  return { objects: [im], update: (f) => { time.value = f.timeS; }, dispose: disposer([im]) };
}

/** Painted Easter eggs in the planters and in little nests on the apron beside the course. */
function eggs(): Built {
  const r = rng(404);
  const colours = ['#ffe066', '#c9a0ff', '#9be27a', '#ffb3d1', '#7fd4ff', '#ff8a5c'].map((c) => new Color(c));
  const spots: Vector3[] = [];
  for (const x0 of standModulesX()) for (let k = 0; k < 4; k++) {
    for (let e = 0; e < 3; e++) spots.push(new Vector3(x0 + 3 + k * 6 + (r() - 0.5) * 1.1, 0.48, STAND.frontZ + 0.75 + (r() - 0.5) * 0.25));
  }
  for (let x = COURSE.from + 8; x < COURSE.to - 4; x += 11) {
    for (let e = 0; e < 5; e++) spots.push(new Vector3(x + (r() - 0.5) * 0.8, 0.07, HOARDING.z - 0.9 + (r() - 0.5) * 0.5));
  }
  const im = new InstancedMesh(new SphereGeometry(1, 12, 10), new MeshStandardMaterial({ roughness: 0.35 }), spots.length);
  const m = new Matrix4(), q = new Quaternion(), s = new Vector3();
  spots.forEach((p, i) => {
    q.setFromAxisAngle(new Vector3(r() - 0.5, 0, r() - 0.5).normalize(), r() * 0.8);
    im.setMatrixAt(i, m.compose(p, q, s.set(0.07, 0.095, 0.07)));
    im.setColorAt(i, colours[Math.floor(r() * colours.length)]!);
  });
  im.castShadow = true; im.receiveShadow = true; im.frustumCulled = false; im.name = 'easter-eggs';
  return { objects: [im], dispose: disposer([im]) };
}

/** The Midsummer bonfire (kokko) on a raft off the lake shore: a log pyre that burns from evening into the light night. */
function bonfire(): Built {
  const at = new Vector3(TRACK_LENGTH * 0.52, LAKE.y, LAKE.shoreZ + 34);
  const group = new Group();
  group.name = 'midsummer-bonfire';
  const wood = new MeshStandardMaterial({ color: '#5a4026', roughness: 0.95 });
  const raft = new Mesh(place(new CylinderGeometry(4.2, 4.4, 0.5, 12), [0, 0.15, 0]), wood);
  const logs: BufferGeometry[] = [];
  for (let i = 0; i < 26; i++) {
    const a = (i / 26) * Math.PI * 2;
    logs.push(place(new CylinderGeometry(0.14, 0.18, 6.2, 6), [Math.cos(a) * 1.3, 3.0, Math.sin(a) * 1.3], [Math.sin(a) * 0.42, 0, -Math.cos(a) * 0.42]));
  }
  const pyre = new Mesh(merge(logs), new MeshStandardMaterial({ color: '#3a2a1a', roughness: 1 }));
  raft.receiveShadow = true;
  group.add(raft, pyre);
  // Flames: three crossed cards with a rising noise flame.
  const time = new Uniform(0), burn = new Uniform(1);
  const flameMat = new MeshBasicMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending, side: DoubleSide });
  flameMat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = time; shader.uniforms.uBurn = burn;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vFlameUv;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFlameUv = uv;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec2 vFlameUv;\nuniform float uTime, uBurn;\n${GLSL_NOISE}`)
      .replace('#include <color_fragment>', `#include <color_fragment>
        vec2 fu = vFlameUv;
        float n = envFbm(vec2(fu.x * 3.0, fu.y * 2.0 - uTime * 1.8));
        float width = (1.0 - fu.y) * 0.5 + 0.05;
        float body = smoothstep(width, width * 0.3, abs(fu.x - 0.5 + (n - 0.5) * 0.25 * fu.y));
        float flame = body * smoothstep(1.0, 0.25, fu.y + (n - 0.5) * 0.5) * uBurn;
        vec3 fc = mix(vec3(4.0, 1.1, 0.15), vec3(5.0, 3.6, 1.2), smoothstep(0.2, 0.8, body * (1.0 - fu.y)));
        diffuseColor = vec4(fc * flame, flame);`);
  };
  flameMat.customProgramCacheKey = () => 'bonfire-flame';
  const flames: BufferGeometry[] = [];
  for (let i = 0; i < 3; i++) flames.push(place(new PlaneGeometry(5, 9), [0, 5.2, 0], [0, (i * Math.PI) / 3, 0]));
  const flame = new Mesh(merge(flames), flameMat);
  group.add(flame);
  group.position.copy(at);
  const sparks = new Particles('sparks', undefined, at.clone().add(new Vector3(0, 6, 0)));
  return {
    objects: [group, sparks.points],
    update: (f) => {
      // The kokko is lit in the evening and burns into the small hours.
      const lit = f.hour >= 20 || f.hour < 3 ? 1 : 0;
      burn.value = lit;
      flame.visible = lit > 0;
      sparks.amount = lit;
      time.value = f.timeS;
      sparks.update(f.timeS, f.camera, f.viewportHeight);
    },
    dispose: () => { disposer([group])(); sparks.dispose(); },
  };
}

function fireworks(): Built {
  const fw = new Fireworks();
  return {
    objects: [fw.points],
    update: (f) => {
      // Only once it is dark enough to see them.
      fw.amount = Math.max(0, (f.night - 0.4) / 0.6);
      fw.update(f.timeS, f.camera.fov ?? 50, f.viewportHeight);
    },
    dispose: () => fw.dispose(),
  };
}
